import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import { playerConfigDomain, publicDataDomain } from '../../util/config';
import { ENTERPRISE_CONFIG_ID, getPlayerBorderRadius, isUsedOnMobile, joinClassNames, SCENE_EDITOR_PLAYER_ACCOUNT_ID } from '../../util/util';
import * as QPlayerSVGElement from '../QPlayerSVGElement';
import i18n from '../../i18n';

import './Poster.scss';

const DEFAULT_COLOR = '#126cfc';
const DEFAULT_PLAY_BUTTON_WIDTH = 120;
const DEFAULT_PLAY_BUTTON_X = 50;
const DEFAULT_PLAY_BUTTON_Y = 50;

interface PosterProps {
	qplayerId: string;
	qplayerMediaId: string;
	qplayerScenarioId: string;
	qplayerSceneId: string;
	qplayerPlaylistId: string;
	qplayerAccountId: string;
	qplayerPresetId: string;
	qplayerPreset: string;
	qplayerLanguage: string;
	qplayerShareUrl: string;
	qplayerInline?: string;
	qplayerSharing?: string;
	qplayerAutoplay?: string;
	qplayerMute?: string;
	qplayerRepeat?: string;
	qplayerDownload?: string;
	qplayerOpenInNewWindow?: string;
	qplayerThumbnailMediaId?: string;
	qplayerThumbnailWidth?: string;
	qplayerThumbnailHeight?: string;
	qplayerAnalytics?: string;
	qplayerIgnoreChat?: string;
	qplayerUniqueFrame?: string;
	qplayerToken?: string;
	qplayerTitle?: string;
	qplayerStartAt?: string;
	qplayerCustomButton?: string;
	qplayerAnalyticsCookies?: string;
	qplayerPlaylistInitPosition?: string;
	qplayerSubtitlesAuto?: string;
	qplayerAspectRatio?: string;

	qplayerMobileDevice?: string;
	qplayerDev?: string;
	qplayerBeta?: string;
	qplayerAlpha?: string;

	containerKey?: string;
	container?: HTMLElement;
    modalKey: string;
}

const ModalPlayerPoster = ({
    qplayerLanguage,
    qplayerMediaId,
    qplayerAccountId,
    qplayerToken,
    qplayerMobileDevice,
    qplayerPresetId,
    qplayerPreset,
    qplayerScenarioId,
    qplayerPlaylistId,
    modalKey,
    qplayerAspectRatio,
    qplayerThumbnailMediaId
}: PosterProps) => {
    const [playerConfigs, setPlayerConfigs] = useState<any | null>(null);
    const [thumbnailUrl, setThumbnail] = useState<string | null>(null);
    const [ready, setReady] = useState<boolean>(false);

    const isOnMobile = useMemo(() => isUsedOnMobile({ mobileDevice: qplayerMobileDevice }), [qplayerMobileDevice]);
    const getRestrictToken = (mediaId: string, tokens: string[], isScenario = false) => {
        let result = null;
        const splitter = isScenario ? '-scenario-' : '-media-';
        if (tokens && tokens.length > 0) {
            tokens.forEach((token: string) => {
                const checks = token.split(splitter);
                result = token.includes(mediaId) ? checks[0] : null;
            });
        }
        return result;
    }

    const setPlayerConfigData = useCallback((configData: any) => {
        if (configData && configData.layouts) {
            const desktop = configData.layouts.find((el: any) => {
                return el.type === 'desktop';
            });

            const mobile = configData.layouts.find((el: any) => {
                return el.type === 'mobile';
            });

            const settings = isOnMobile ? mobile : desktop;
            const language = qplayerLanguage ? qplayerLanguage : settings?.accessibilitySettings?.language;
            if (language !== i18n.language) {
                i18n.changeLanguage(language);
            }

            setTimeout(() => {
                setReady(true);
            }, 2000);
            setPlayerConfigs(settings);
        }
    }, [qplayerLanguage, isOnMobile]);

    const fetchPlayerConfigData = () => {
		let configUrl = qplayerPreset ? qplayerPreset : (qplayerAccountId ? `${playerConfigDomain}${qplayerAccountId}/settings/${qplayerPresetId || ENTERPRISE_CONFIG_ID}` : "");
		const enterpriseConfigUrl = `${playerConfigDomain}${qplayerAccountId ?? SCENE_EDITOR_PLAYER_ACCOUNT_ID}/settings/${ENTERPRISE_CONFIG_ID}`;

		if (!configUrl) {
			return;
		}

		axios.get(configUrl).then((response) => {
			if (response?.data) {
				setPlayerConfigData(response?.data);
			} else {
				axios.get(enterpriseConfigUrl).then((response) => {
					response?.data && setPlayerConfigData(response?.data);
				});
			}
		}).catch((_error) => {
			axios.get(enterpriseConfigUrl).then((response) => {
				response?.data && setPlayerConfigData(response?.data);
			});
		});
    }

    const getThumbnailFromMedia = (mediaId: string) => {
        let dataUrl = publicDataDomain + qplayerAccountId + '/medias/' + mediaId;
        const token = qplayerToken ? getRestrictToken(mediaId, [qplayerToken as string], false) : null;
        const headers = token
            ? {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Authorization': 'AVP1 ' + token,
                    },
                }
            : undefined;

        fetch(dataUrl, headers)
            .then((res) => res.json())
            .then((data) => {
                if (!data || typeof data === 'string') {
                    return;
                }

                const thumbnailElement = data.asset?.resources?.filter((el: any) => el.type === 'image');
                const thumbnailUrl = thumbnailElement[0].renditions[0].links[0].href;
                setThumbnail(thumbnailUrl);
            });
    }

    const getThumbnailFromHomeScene = () => {
            const dataUrl = publicDataDomain + qplayerAccountId + '/scenarios/' + qplayerScenarioId;
            const token = qplayerToken ? getRestrictToken(qplayerScenarioId, [qplayerToken as string], true) : null;
			const headers = token
				? {
						headers: {
							'Content-Type': 'application/json',
							'X-Authorization': 'AVP1 ' + token,
						},
					}
				: undefined;

			fetch(dataUrl, headers)
				.then((res) => res.json())
				.then((scenario) => {
					if (!scenario || typeof scenario === 'string') {
						return;
					}

					let bgMediaId = '';
					const homeScene = (scenario.scenes ?? []).find((scene: any) => scene.id === (scenario.defaults ?? {}).sceneId);
					const widgetTemplates = homeScene?.widgetTemplates ?? [];
					let currentLayout = (homeScene?.layouts ?? []).find((l: any) => l.type === (scenario?.defaults?.type ?? 'desktop')) ?? {};
					currentLayout = currentLayout ?? homeScene?.layouts?.[0] ?? {};
					const backgroundWidgets = (currentLayout?.boxes ?? [])?.[0]?.widgets ?? [];

					widgetTemplates.forEach((template: any) => {
						if (backgroundWidgets.find((bg: any) => bg?.widgetTemplateId === template?.id)) {
							if (template?.type === 'video') {
								bgMediaId = template?.settings?.mediaId;
							}
						}
					});

					if (!bgMediaId) {
						return;
					}
					getThumbnailFromMedia(bgMediaId);
				});
		}

    const [isPlayButtonHover, setPlayButtonHover] = useState(false);
    const [isPlayButtonFocus, setPlayButtonFocus] = useState(false);
    const [thumbnailDimension, setThumbnailDimension] = useState({ width: 0, height: 0 });

    const thumbnailRef = useRef<HTMLDivElement>(null);

    const playBtnConfig = useMemo(() => {
        const color = playerConfigs?.playerStyling?.primaryColor ?? DEFAULT_COLOR;
        const icon = (playerConfigs?.playCover?.customPlayButtonIcon ? playerConfigs?.playCover?.customPlayButtonUrl : '') ?? '';
        const btnWidth = playerConfigs?.playCover?.playButtonSizeWidth ?? DEFAULT_PLAY_BUTTON_WIDTH;
        const btnHeight = icon ? null : btnWidth * 0.6;

        const xByPercentage = playerConfigs?.playCover?.playButtonPositionX ?? DEFAULT_PLAY_BUTTON_X;
        const yByPercentage = playerConfigs?.playCover?.playButtonPositionY ?? DEFAULT_PLAY_BUTTON_Y;

        const isShowVideoLength = icon || qplayerScenarioId ? false : playerConfigs?.playCover?.showVideoLength ?? false;

        return { color, icon, width: btnWidth, height: btnHeight, x: xByPercentage, y: yByPercentage, isShowVideoLength };
    }, [playerConfigs, qplayerScenarioId, thumbnailDimension]);

    const handlePlay = () => {
        const qplayerEmbedModal = document.querySelector(`qplayer-embed[data-qplayer-key="${modalKey}"]`) as HTMLElement;
        qplayerEmbedModal?.setAttribute('data-qplayer-modal-open', 'true');
        const qplayerAppElem = qplayerEmbedModal?.querySelector('div.qplayer-app') as any;
        const visiblePlayer = qplayerEmbedModal?.querySelector('div.main-player--visible') as any;

        if (visiblePlayer && visiblePlayer.id) {
            const playerId = visiblePlayer.id.split('-container')[0];
            (window as any).GoBrain.widgets(playerId)?.play?.();
        }

        setTimeout(() => {
		    qplayerAppElem && qplayerAppElem.focus();
        }, 100);
    };

    useEffect(() => {
        thumbnailRef.current &&
            new ResizeObserver((changes) => {
                changes.forEach((change) => {
                    const { contentRect } = change;

                    if (contentRect?.width && contentRect?.height) {
                        setThumbnailDimension({ width: contentRect?.width, height: contentRect?.height });
                    }
                });
            }).observe(thumbnailRef.current);
    }, []);

    const renderPlayButton = () => playBtnConfig.icon ? (
            <div
                className="play-button"
                tabIndex={0}
                role="button"
                aria-label={i18n.t('PLAY')}
                style={{
                    top: `${playBtnConfig.y}%`,
                    left: `${playBtnConfig.x}%`,
                    width: `${playBtnConfig.width}px`,
                    height: playBtnConfig.height ?? 'auto',
                }}
                onMouseEnter={isOnMobile ? undefined : () => setPlayButtonHover(true)}
                onMouseLeave={isOnMobile ? undefined : () => setPlayButtonHover(false)}
                onFocus={isOnMobile ? undefined : () => setPlayButtonFocus(true)}
                onBlur={isOnMobile ? undefined : () => setPlayButtonFocus(false)}
                onClick={handlePlay}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handlePlay();
                    }
                }}
            >
                <div className="play-icon full-width" aria-hidden={true}>
                    <img src={playBtnConfig.icon} alt="play-icon" />
                    <div
                        className="custom-icon-overlay"
                        style={{
                            backgroundColor: isPlayButtonHover || isPlayButtonFocus ? playBtnConfig.color : 'transparent',
                            WebkitMaskImage: `url(${playBtnConfig.icon})`,
                            maskImage: `url(${playBtnConfig.icon})`,
                            maskSize: 'contain',
                            maskRepeat: 'no-repeat',
                            maskPosition: 'center',
                        }}
                    />
                </div>
            </div>
        ) : (
            <div
                className={`play-button${isPlayButtonHover ? ' hovered' : ''}`}
                tabIndex={0}
                role="button"
                aria-label={i18n.t('PLAY')}
                style={{
                    backgroundColor: isPlayButtonHover || isPlayButtonFocus ? playBtnConfig.color : '#000000cc',
                    top: `${playBtnConfig.y}%`,
                    left: `${playBtnConfig.x}%`,
                    width: `${playBtnConfig.width}px`,
                    height: `${playBtnConfig.height}px`,
                    color: '#ffffff'
                }}
                onMouseEnter={isOnMobile ? undefined : () => setPlayButtonHover(true)}
                onMouseLeave={isOnMobile ? undefined : () => setPlayButtonHover(false)}
                onFocus={isOnMobile ? undefined : () => setPlayButtonFocus(true)}
                onBlur={isOnMobile ? undefined : () => setPlayButtonFocus(false)}
                onClick={handlePlay}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handlePlay();
                    }
                }}
            >
                <div className="play-icon" aria-hidden={true}>
                    <QPlayerSVGElement.Play />
                </div>
            </div>
    );

    useEffect(() => {
        !playerConfigs && fetchPlayerConfigData();

        if (thumbnailUrl) {
            return;
        }
        const thumbnailMediaId = qplayerThumbnailMediaId ?? qplayerMediaId ?? qplayerPlaylistId;
        if (thumbnailMediaId) {
            getThumbnailFromMedia(thumbnailMediaId);
        } else if (qplayerScenarioId) {
            getThumbnailFromHomeScene();
        }
    }, [fetchPlayerConfigData, getThumbnailFromMedia, getThumbnailFromHomeScene]);

    // Rounded corners from the preset (Player styling), same as the inline embed (App.tsx).
    const playerBorderRadius = getPlayerBorderRadius(playerConfigs);

    return (<div
        className={joinClassNames(`qplayer-poster`, !thumbnailUrl?.trim() ? 'qplayer-poster--no-thumbnail' : '')}
        ref={thumbnailRef}
        style={{...(qplayerAspectRatio ? { aspectRatio: qplayerAspectRatio, position: "relative", height: "auto" } : {}), ...((thumbnailUrl ?? "").trim() ? { backgroundImage: `url('${thumbnailUrl}')`, } : undefined), ...(playerBorderRadius ? { borderRadius: playerBorderRadius } : {})}}
    >
        {ready && renderPlayButton()}
    </div>);
};

export default ModalPlayerPoster;
